import { KeyOutlined, ProjectOutlined } from '@ant-design/icons';
import type { HqApi } from '@hq/api-sdk';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Empty, Select, Space, Tag, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { ScreenDeviceList } from './ScreenDeviceList';
import { ScreenDevicePendingPanel } from './ScreenDevicePendingPanel';

export interface ScreenDevicePageProps {
  /** 已登录会话的 SDK 实例（各端注入，复用其 baseUrl 与令牌）。 */
  hq: HqApi;
  /**
   * 跳转「申请码确认」。路由由端内决定（`/screen-scan` 与 `/system/screen-scan` 不同），
   * 包内不引 react-router，避免把路由实例带进共享包。
   */
  onScan: () => void;
  /**
   * 打开本端内置大屏（各站点的 `/screen` 全屏路由，新窗口）；
   * 未提供的端（尚未接入大屏的门户）不传则不展示按钮。
   */
  onOpenScreen?: () => void;
  /** 卡片标题：HOSPITAL/GOV/OPS 用「我的大屏」，SYSTEM 用「全部大屏」。 */
  title?: string;
  /** 列表上方说明文案（各端措辞不同）。 */
  hint?: string;
  /** 是否显示「吊销」：四端均可（后端按门户限定范围），保留开关便于将来收紧。 */
  canDisable?: boolean;
  /**
   * 平台视角（SYSTEM）：先选租户再看该租户的设备。
   * 该分支会调用 `tenant.list`，只有平台门户有权限，因此不得在租户端打开。
   */
  platform?: boolean;
  /** 是否显示「待确认申请」只读监控面板（仅 SYSTEM/OPS 有跨租户查询权限）。 */
  showPending?: boolean;
}

/**
 * 大屏设备页（四端共用）：
 * 取数凭据只有一条路径——大屏显示 12 位申请码 → 电脑端「申请码确认」填入 → 屏端领取凭据（可逐屏吊销）。
 * 数据范围完全由后端按令牌（门户 + 租户 + 机构子树）裁剪，前端不做二次过滤。
 */
export function ScreenDevicePage({
  hq,
  onScan,
  onOpenScreen,
  title = '我的大屏',
  hint,
  canDisable = false,
  platform = false,
  showPending = false
}: ScreenDevicePageProps) {
  const [tenantId, setTenantId] = useState('');

  const tenants = useQuery({
    queryKey: ['screen-tenants'],
    queryFn: () => hq.tenant.list({}),
    enabled: platform
  });

  const current = useMemo(
    () => tenants.data?.find((item) => item.id === tenantId) ?? tenants.data?.[0],
    [tenantId, tenants.data]
  );

  const tenantSelect = platform ? (
    <Space size={8}>
      <Typography.Text type="secondary">归属租户</Typography.Text>
      <Select
        style={{ width: 260 }}
        placeholder="请选择租户"
        loading={tenants.isLoading}
        value={current?.id}
        onChange={setTenantId}
        showSearch
        optionFilterProp="label"
        options={(tenants.data ?? []).map((item) => ({
          value: item.id,
          label: `${item.tenantName}（${item.tenantCode}）`
        }))}
      />
    </Space>
  ) : undefined;

  return (
    <>
      <Space style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        {onOpenScreen ? (
          <Button icon={<ProjectOutlined />} onClick={onOpenScreen}>
            打开大屏
          </Button>
        ) : null}
        <Button type="primary" icon={<KeyOutlined />} onClick={onScan}>
          申请码确认
        </Button>
      </Space>

      {/* 待确认申请与租户无关（申请阶段还没确定归属），故放在租户选择之外，只做只读监控 */}
      {showPending ? <ScreenDevicePendingPanel hq={hq} /> : null}

      <Card title={title} extra={tenantSelect}>
        {platform ? (
          tenants.error ? (
            <Alert type="error" showIcon message={String(tenants.error)} />
          ) : current ? (
            <>
              <Space size={8} style={{ marginBottom: 12 }}>
                <Tag color={current.status === 'RUNNING' ? 'green' : 'default'}>{current.status}</Tag>
                <Typography.Text type="secondary">监管单位：{current.superviseOrgName ?? '—'}</Typography.Text>
              </Space>
              <ScreenDeviceList hq={hq} tenantId={current.id} canDisable={canDisable} hint={hint} />
            </>
          ) : (
            <Empty description={tenants.isLoading ? '加载中…' : '暂无租户'} />
          )
        ) : (
          <ScreenDeviceList hq={hq} canDisable={canDisable} hint={hint} />
        )}
      </Card>
    </>
  );
}
